import { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, IconButton, Slide } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InstallMobileIcon from '@mui/icons-material/InstallMobile';
import { useSelector } from 'react-redux';
import type { RootState } from '../store';
import { useGetLojaAtualQuery } from '../api';
import { ehDispositivoFixo } from '../lib/canal';
import { ativarPwaKiosk, assinarInstall, instalarPwa, podeInstalar } from '../lib/pwa-kiosk';
import { haImpressaoEmAndamento } from '../lib/impressao-pedido-totem';

/**
 * Convite para instalar o app (PWA) — SÓ no modo tablet/totem. Ativa o
 * manifest + service worker apenas nesses modos (o cliente comum nunca fica
 * instalável) e mostra um banner discreto e dispensável quando o navegador
 * permite instalar. Fica montado na raiz pública ([routes]).
 */
export function PwaKioskInstall(): JSX.Element | null {
  const { data: loja } = useGetLojaAtualQuery();
  const ehKiosk = !!loja && ehDispositivoFixo(loja.identificador);
  const [disponivel, setDisponivel] = useState(false);
  const [dispensado, setDispensado] = useState(false);

  // Carrinho por ref: quem decide a hora de trocar de versão é um callback que
  // vive fora do React (dentro do registro do service worker) e precisa do valor
  // do momento, não do valor que existia quando o efeito rodou.
  const itensNoCarrinho = useSelector((s: RootState) => s.carrinho.itens.length);
  const refItens = useRef(itensNoCarrinho);
  refItens.current = itensNoCarrinho;

  // Ativa o PWA (manifest + SW) apenas quando o aparelho é tablet/totem.
  useEffect(() => {
    // Versão nova só entra com o aparelho ocioso: recarregar no meio do pedido
    // apagaria o carrinho na cara do cliente.
    // Nem com o totem Android ainda imprimindo o pedido: recarregar perderia a NFC-e que está para sair.
    if (ehKiosk) ativarPwaKiosk({ podeRecarregar: () => refItens.current === 0 && !haImpressaoEmAndamento() });
  }, [ehKiosk]);

  // Acompanha a disponibilidade do convite de instalação.
  useEffect(() => {
    const sincronizar = (): void => setDisponivel(podeInstalar());
    sincronizar();
    return assinarInstall(sincronizar);
  }, []);

  if (!ehKiosk || !disponivel || dispensado) return null;

  async function instalar(): Promise<void> {
    const desfecho = await instalarPwa();
    if (desfecho !== 'dismissed') setDispensado(true);
  }

  return (
    <Slide in direction="down">
      <Box sx={{ position: 'fixed', top: 8, left: 8, right: 8, zIndex: (t) => t.zIndex.snackbar }}>
        <Alert
          severity="info"
          variant="filled"
          icon={<InstallMobileIcon />}
          action={
            <>
              <Button color="inherit" size="small" onClick={() => void instalar()}>
                Instalar
              </Button>
              <IconButton color="inherit" size="small" aria-label="Dispensar" onClick={() => setDispensado(true)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </>
          }
        >
          Instale o app neste aparelho para abrir em tela cheia.
        </Alert>
      </Box>
    </Slide>
  );
}
